import { errorMessage } from '../services/apiError';
import {
  BillingSummary,
  TasksByProjectResponse,
  CreditLedgerEntry,
  CreditLedgerDirection,
  CreditLedgerHistoryResponse,
  CreditLedgerPaginationInfo,
} from '../services/billingService';
import { OutstandingBalance, PaymentHistoryItem } from '../services/paymentService';
import { Project } from '../services/projectService';

export const formatCurrency = (amount: number, currency = 'USD'): string => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency,
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
}).format(amount);

export const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '-';

  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export const getDefaultDateRange = (): { start: Date; end: Date } => {
  const today = new Date();
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  return { start: startOfMonth, end: today };
};

export const formatStatus = (status: string): string => {
  const statusMap: Record<string, string> = {
    backlog: 'Backlog',
    todo: 'To Do',
    in_progress: 'In Progress',
    blocked: 'Blocked',
    review: 'Review',
    client_review: 'Client Review',
    done: 'Done',
  };

  return statusMap[status] || status;
};

export const getStatusClass = (status: string): string => {
  const classMap: Record<string, string> = {
    backlog: 'status-backlog',
    todo: 'status-todo',
    in_progress: 'status-in-progress',
    blocked: 'status-blocked',
    review: 'status-review',
    client_review: 'status-review',
    done: 'status-done',
  };

  return classMap[status] || '';
};

export const getPaymentStatusClass = (status: string): string => {
  const classMap: Record<string, string> = {
    pending: 'payment-status-pending',
    processing: 'payment-status-processing',
    completed: 'payment-status-completed',
    failed: 'payment-status-failed',
    refunded: 'payment-status-refunded',
  };

  return classMap[status] || '';
};

const findProjectTitle = (allProjects: { id: string; title: string | null }[], projectId: string) => {
  const project = allProjects.find((p) => p.id === projectId);

  return project?.title || '1 Project';
};

export const getFilterButtonText = (
  selectedProjectIds: string[],
  allProjects: { id: string; title: string | null }[]
): string => {
  if (selectedProjectIds.length === 0) return 'All Projects';
  if (selectedProjectIds.length === 1) return findProjectTitle(allProjects, selectedProjectIds[0]);

  return `${selectedProjectIds.length} Projects`;
};

interface BillingService {
  getSummary: () => Promise<BillingSummary>;
  getTasksByProject: (
    projectIds?: string[], page?: number, pageSize?: number, dateFrom?: string | null, dateTo?: string | null
  ) => Promise<TasksByProjectResponse>;
  getLedgerHistory: (
    page?: number,
    pageSize?: number,
    dateFrom?: string | null,
    dateTo?: string | null,
    direction?: CreditLedgerDirection
  ) => Promise<CreditLedgerHistoryResponse>;
}

interface ProjectService {
  getProjects: () => Promise<Project[]>;
}

interface PaymentService {
  getOutstandingBalance: () => Promise<OutstandingBalance>;
  getHistory: (limit?: number) => Promise<PaymentHistoryItem[]>;
}

const fetchAllBillingData = async (
  billingService: BillingService,
  projectService: ProjectService,
  paymentService: PaymentService,
  ledgerPage: number,
  ledgerPageSize: number,
  ledgerDateFrom?: string | null,
  ledgerDateTo?: string | null,
  ledgerDirection: CreditLedgerDirection = 'all'
) => {
  const [summaryData, projectsData, balanceData, paymentHistoryData, ledgerData] = await Promise.all([
    billingService.getSummary(),
    projectService.getProjects(),
    paymentService.getOutstandingBalance(),
    paymentService.getHistory(10),
    billingService.getLedgerHistory(ledgerPage, ledgerPageSize, ledgerDateFrom, ledgerDateTo, ledgerDirection),
  ]);

  return { summaryData, projectsData, balanceData, paymentHistoryData, ledgerData };
};

const fetchTasksByProject = async (
  selectedProjectIds: string[],
  currentPage: number,
  pageSize: number,
  billingService: BillingService,
  dateFrom?: string | null,
  dateTo?: string | null
) => {
  const tasksByProject = await billingService.getTasksByProject(
    selectedProjectIds.length > 0 ? selectedProjectIds : undefined,
    currentPage,
    pageSize,
    dateFrom,
    dateTo
  );

  return tasksByProject;
};

// Helper to load billing data
const loadBillingDataHelper = async (
  selectedProjectIds: string[],
  currentPage: number,
  pageSize: number,
  billingService: BillingService,
  projectService: ProjectService,
  paymentService: PaymentService,
  ledgerPage: number,
  ledgerPageSize: number,
  dateFrom?: string | null,
  dateTo?: string | null,
  ledgerDateFrom?: string | null,
  ledgerDateTo?: string | null,
  ledgerDirection: CreditLedgerDirection = 'all'
) => {
  const baseData = await fetchAllBillingData(
    billingService,
    projectService,
    paymentService,
    ledgerPage,
    ledgerPageSize,
    ledgerDateFrom,
    ledgerDateTo,
    ledgerDirection
  );
  const tasksByProject = await fetchTasksByProject(
    selectedProjectIds, currentPage, pageSize, billingService, dateFrom, dateTo
  );

  return { ...baseData, tasksByProject };
};

interface PaymentSuccessResponse {
  paymentId: string;
  credits: number;
}

// Helper to handle payment success
export const handlePaymentSuccessHelper = (
  response: PaymentSuccessResponse,
  setPaymentSuccess: (msg: string) => void,
  loadBillingData: () => void
) => {
  setPaymentSuccess(`Payment successful! ${response.credits} credits paid.`);
  loadBillingData();
};

type StateSetters = {
  setSummary: (data: BillingSummary) => void;
  setAllProjects: (data: Project[]) => void;
  setOutstandingBalance: (data: OutstandingBalance) => void;
  setPaymentHistory: (data: PaymentHistoryItem[]) => void;
  setLedgerHistory: (data: CreditLedgerEntry[]) => void;
  setLedgerPagination: (data: CreditLedgerPaginationInfo) => void;
  setProjectGroups: (data: TasksByProjectResponse['projects']) => void;
  setPagination: (data: TasksByProjectResponse['pagination']) => void;
  setTotals: (data: TasksByProjectResponse['totals']) => void;
};

const applyBaseData = (
  summaryData: BillingSummary,
  projectsData: Project[],
  balanceData: OutstandingBalance,
  paymentHistoryData: PaymentHistoryItem[],
  ledgerData: CreditLedgerHistoryResponse,
  setters: Pick<
    StateSetters,
    'setSummary' | 'setAllProjects' | 'setOutstandingBalance' | 'setPaymentHistory' |
    'setLedgerHistory' | 'setLedgerPagination'
  >
) => {
  setters.setSummary(summaryData);
  setters.setAllProjects(projectsData);
  setters.setOutstandingBalance(balanceData);
  setters.setPaymentHistory(paymentHistoryData);
  setters.setLedgerHistory(ledgerData.entries);
  setters.setLedgerPagination(ledgerData.pagination);
};

const applyTasksData = (
  tasksByProject: TasksByProjectResponse,
  setters: Pick<StateSetters, 'setProjectGroups' | 'setPagination' | 'setTotals'>
) => {
  setters.setProjectGroups(tasksByProject.projects);
  setters.setPagination(tasksByProject.pagination);
  setters.setTotals(tasksByProject.totals);
};

// Helper to apply loaded data to state setters
const applyBillingDataToState = (
  data: {
    summaryData: BillingSummary;
    projectsData: Project[];
    balanceData: OutstandingBalance;
    paymentHistoryData: PaymentHistoryItem[];
    ledgerData: CreditLedgerHistoryResponse;
    tasksByProject: TasksByProjectResponse;
  },
  setters: StateSetters
) => {
  applyBaseData(
    data.summaryData, data.projectsData, data.balanceData, data.paymentHistoryData, data.ledgerData, setters
  );
  applyTasksData(data.tasksByProject, setters);
};

const setLoadingStates = (
  setLoading: (loading: boolean) => void,
  setError: (error: string | null) => void,
  setPaymentSuccess: (msg: string | null) => void
) => {
  setLoading(true);
  setError(null);
  setPaymentSuccess(null);
};

const handleBillingLoadError = (
  err: unknown,
  setError: (error: string | null) => void
) => {
  setError(errorMessage(err, 'We couldn’t load your billing information. Refresh and try again.'));
};

const performBillingDataLoad = async (
  selectedProjectIds: string[],
  currentPage: number,
  pageSize: number,
  billingService: BillingService,
  projectService: ProjectService,
  paymentService: PaymentService,
  setters: StateSetters,
  ledgerPage: number,
  ledgerPageSize: number,
  dateFrom?: string | null,
  dateTo?: string | null,
  ledgerDateFrom?: string | null,
  ledgerDateTo?: string | null,
  ledgerDirection: CreditLedgerDirection = 'all'
) => {
  const data = await loadBillingDataHelper(
    selectedProjectIds,
    currentPage,
    pageSize,
    billingService,
    projectService,
    paymentService,
    ledgerPage,
    ledgerPageSize,
    dateFrom,
    dateTo,
    ledgerDateFrom,
    ledgerDateTo,
    ledgerDirection
  );

  applyBillingDataToState(data, setters);
};

type ExecuteBillingDataLoadOptions = {
  selectedProjectIds: string[];
  currentPage: number;
  pageSize: number;
  ledgerPage: number;
  ledgerPageSize: number;
  billingService: BillingService;
  projectService: ProjectService;
  paymentService: PaymentService;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setPaymentSuccess: (msg: string | null) => void;
  setters: StateSetters;
  dateFrom?: string | null;
  dateTo?: string | null;
  ledgerDateFrom?: string | null;
  ledgerDateTo?: string | null;
  ledgerDirection?: CreditLedgerDirection;
};

// Complete billing data load with error handling
export const executeBillingDataLoad = async (options: ExecuteBillingDataLoadOptions) => {
  try {
    setLoadingStates(options.setLoading, options.setError, options.setPaymentSuccess);
    await performBillingDataLoad(
      options.selectedProjectIds,
      options.currentPage,
      options.pageSize,
      options.billingService,
      options.projectService,
      options.paymentService,
      options.setters,
      options.ledgerPage,
      options.ledgerPageSize,
      options.dateFrom,
      options.dateTo,
      options.ledgerDateFrom,
      options.ledgerDateTo,
      options.ledgerDirection
    );
  } catch (err) {
    handleBillingLoadError(err, options.setError);
  } finally {
    options.setLoading(false);
  }
};
